import React, {useCallback, useMemo} from "react"
import PhoneInput from 'react-phone-number-input/input'
import type {Country} from 'react-phone-number-input'
import css from './aio.less'
import cn from 'classnames'
import icons from "../../../markons/markons";
import AioIcon from './AioIcon'
import type {PhoneNumber,CountryCode} from 'libphonenumber-js';
import {getCountryCallingCode,parsePhoneNumber} from 'libphonenumber-js'
import AioLabel from './AioLabel'
import {useTestId} from '../../contexts/testid'


interface PhoneInterface {
    value: string
    phone?: PhoneNumber
    toJSON?(): string
}


interface AioPhoneOnlyProps {
    value: string|PhoneInterface
    onChange: CompatEventHandler
    disabled?: boolean
    loading?: boolean
    inline?: boolean
    width?: number | string
    minWidth?: number | string
    maxWidth?: number | string
    label: string
    icon?: string
    required?: boolean
    testId?: string
}

const DEFAULT_COUNTRY = 'US'


function tryParsePhoneNumber(number: string, defaultCountry: string) {
    try {
        return parsePhoneNumber(number, defaultCountry as CountryCode)
    } catch(_){
        // Ignore.
    }
    return null;
}

function tryGetCountryCallingCode(country: string) {
    try {
        return getCountryCallingCode(country as CountryCode)
    } catch(_){
        // Ignore.
    }
    return null;
}

export default function AioPhoneOnly(props: AioPhoneOnlyProps) {
    const defaultCountry = window.company?.countryCode ? window.company.countryCode as Country : DEFAULT_COUNTRY
    const testId = useTestId(props.testId)

    const phone = useMemo(() => {
        if(!props.value) return {value:''}
        if(typeof props.value === 'string') {
            const parsed = tryParsePhoneNumber(props.value, defaultCountry)
            if(parsed) {
                return {value: parsed.number}
            }
            return {value: props.value}
        }
        return props.value
    }, [props.value])

    const numberOnChange = useCallback(value => {
        props.onChange({
            target: {
                value: {
                    value,
                    toJSON() {
                        if(!this.value) return ''
                        const parsed = tryParsePhoneNumber(this.value, defaultCountry)
                        if(parsed) {
                            if(parsed.getType() !== 'TOLL_FREE' && parsed.countryCallingCode === tryGetCountryCallingCode(defaultCountry)) {
                                return parsed.formatNational()
                            }
                            return parsed.formatInternational()
                        }
                        return this.value;
                    },
                }
            }
        })
    }, [props.onChange])

    return (
        <div className={cn(css.wrapper, {
            [css.hasIcon]: true,
            [css.disabled]: props.disabled,
            [css.loading]: props.loading,
            [css.inline]: props.inline
        })}
             style={{width: props.width, minWidth: props.minWidth, maxWidth: props.maxWidth}}>
            <PhoneInput defaultCountry={defaultCountry} value={phone.value} onChange={numberOnChange}
                        disabled={props.disabled} className={css.text} data-testid={testId} />
            <AioIcon name={props.icon || icons.faPhoneAlt}/>
            <AioLabel className={css.label} required={props.required}>{props.label}</AioLabel>
        </div>
    )
}
